"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { LngLatBounds, LngLatLike, type Map as MapLibreMap } from "maplibre-gl";
import {
  Map as MapView,
  Layer,
  MapRef,
  NavigationControl,
  Source,
  type MapLayerMouseEvent,
} from "@vis.gl/react-maplibre";
import type { Feature, FeatureCollection, Polygon } from "geojson";
import { useCustomParkings } from "../hooks/useCustomParkings";
import {
  calculatePolygonCentroid,
  type ParkingPolygon,
  type StoredCustomParkingRecord,
} from "../lib/customParkingStorage";
import { PARKING_HOVER_EVENT, type ParkingHoverEventDetail } from "../lib/parkingEvents";
import type { ParkingFootprint } from "../lib/parkingTypes";

const DEFAULT_LIGHT_STYLE_URL = "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json";
const DEFAULT_DARK_STYLE_URL = "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
const LIGHT_STYLE_URL = process.env.REACT_APP_MAP_LIGHT_STYLE_URL ?? DEFAULT_LIGHT_STYLE_URL;
const DARK_STYLE_URL = process.env.REACT_APP_MAP_DARK_STYLE_URL ?? DEFAULT_DARK_STYLE_URL;

const debugLog = (...args: unknown[]) => {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  // eslint-disable-next-line no-console
  console.log("[MapCard]", ...args);
};

const ensureWorkerClass = () => {
  if (typeof window === "undefined") {
    return;
  }

  const globalAny = maplibregl as typeof maplibregl & { workerClass?: typeof Worker };

  if (globalAny.workerClass) {
    return;
  }

  globalAny.workerClass = class MapLibreWorker extends Worker {
    constructor() {
      super(new URL("maplibre-gl/dist/maplibre-gl-csp-worker.js", import.meta.url), { type: "module" });
    }
  } as typeof Worker;
};

type PolygonVariant = "default" | "highlight" | "custom";

export type MapMarker = {
  id: string;
  label: string;
  lngLat: LngLatLike;
  street?: string;
  variant?: "default" | "highlight";
  footprint?: ParkingFootprint;
};

type MapCardProps = {
  markers: MapMarker[];
  className?: string;
  selectedMarkerId?: string | null;
  onMarkerSelect?: (markerId: string | null) => void;
  keepAllPolygonsVisible?: boolean;
  focusSelectedMarker?: boolean;
};

type PolygonFeatureProperties = {
  id: string;
  label: string;
  street?: string;
  variant: PolygonVariant;
  source: "base" | "custom";
  active: boolean;
};

type MapPolygonEntry = {
  id: string;
  label: string;
  street?: string;
  variant: PolygonVariant;
  coordinates: ParkingPolygon;
  centroid: [number, number];
  source: "base" | "custom";
};

const ensureClosedPolygon = (polygon: ParkingPolygon): ParkingPolygon => {
  if (polygon.length === 0) {
    return [];
  }
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return polygon;
  }
  return [...polygon, first];
};

const fallbackPolygon = (lngLat: LngLatLike): ParkingPolygon => {
  const point = maplibregl.LngLat.convert(lngLat);
  const baseOffset = 0.00018;
  const latOffset = baseOffset;
  const lngOffset = baseOffset / Math.max(Math.cos((point.lat * Math.PI) / 180), 0.2);

  return [
    [point.lng - lngOffset, point.lat - latOffset],
    [point.lng - lngOffset, point.lat + latOffset],
    [point.lng + lngOffset, point.lat + latOffset],
    [point.lng + lngOffset, point.lat - latOffset],
  ];
};

const buildFeatureCollection = (
  polygons: MapPolygonEntry[],
  activeId: string | null,
): FeatureCollection<Polygon, PolygonFeatureProperties> => {
  const features: Feature<Polygon, PolygonFeatureProperties>[] = polygons.map((polygon) => ({
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [polygon.coordinates],
    },
    properties: {
      id: polygon.id,
      label: polygon.label,
      street: polygon.street,
      variant: polygon.variant,
      source: polygon.source,
      active: polygon.id === activeId,
    },
  }));

  return {
    type: "FeatureCollection",
    features,
  };
};

const extendBoundsWithPolygon = (bounds: LngLatBounds | null, polygon: ParkingPolygon): LngLatBounds | null => {
  if (polygon.length === 0) {
    return bounds;
  }

  const [firstLng, firstLat] = polygon[0];
  const nextBounds = bounds ?? new LngLatBounds([firstLng, firstLat], [firstLng, firstLat]);

  polygon.forEach(([lng, lat]) => {
    nextBounds.extend([lng, lat]);
  });

  return nextBounds;
};

const focusOnPolygon = (map: MapLibreMap, polygon: MapPolygonEntry) => {
  const bounds = extendBoundsWithPolygon(null, polygon.coordinates);
  if (bounds) {
    map.fitBounds(bounds, { padding: 64, maxZoom: 17, duration: 500 });
    return;
  }
  const [lng, lat] = polygon.centroid ?? polygon.coordinates[0];
  const target = maplibregl.LngLat.convert([lng, lat]);
  map.easeTo({ center: target, zoom: Math.max(map.getZoom(), 14), duration: 600 });
};

const fitMapToPolygons = (map: MapLibreMap, polygons: MapPolygonEntry[]) => {
  if (polygons.length === 0) {
    return;
  }

  if (polygons.length === 1) {
    focusOnPolygon(map, polygons[0]);
    return;
  }

  let bounds: LngLatBounds | null = null;
  polygons.forEach((polygon) => {
    bounds = extendBoundsWithPolygon(bounds, polygon.coordinates);
  });
  if (bounds) {
    map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 600 });
  }
};

const emitHoverEvent = (markerId: string | null) => {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new CustomEvent<ParkingHoverEventDetail>(PARKING_HOVER_EVENT, { detail: { markerId } }));
};

export function MapCard({
  markers,
  className,
  selectedMarkerId,
  onMarkerSelect,
  keepAllPolygonsVisible = false,
  focusSelectedMarker = true,
}: MapCardProps) {
  ensureWorkerClass();

  const [mapReady, setMapReady] = useState(false);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const { records: customRecords } = useCustomParkings();
  const markerSignature = useMemo(() => markers.map((marker) => marker.id).join("|"), [markers]);

  const resolveStyleUrl = useCallback(() => {
    if (typeof document !== "undefined" && document.documentElement.classList.contains("dark")) {
      return DARK_STYLE_URL;
    }
    return LIGHT_STYLE_URL;
  }, []);

  const basePolygons = useMemo<MapPolygonEntry[]>(() => {
    return markers.map((marker) => {
      const candidate = Array.isArray(marker.footprint?.polygon) ? marker.footprint?.polygon : undefined;
      const polygon =
        candidate && candidate.length >= 3
          ? ensureClosedPolygon(candidate as ParkingPolygon)
          : ensureClosedPolygon(fallbackPolygon(marker.lngLat));
      const centroidFromPolygon = calculatePolygonCentroid(polygon);
      const fallbackCenter = maplibregl.LngLat.convert(marker.lngLat).toArray() as [number, number];
      const centroid = centroidFromPolygon ?? fallbackCenter ?? polygon[0];
      return {
        id: marker.id,
        label: marker.label,
        street: marker.street,
        variant: marker.variant === "highlight" ? "highlight" : "default",
        coordinates: polygon,
        centroid: centroid as [number, number],
        source: "base" as const,
      };
    });
  }, [markers]);

  const customPolygons = useMemo<MapPolygonEntry[]>(() => {
    return customRecords.flatMap((record: StoredCustomParkingRecord) =>
      record.areaMeta.map((area) => {
        const coordinates = ensureClosedPolygon(area.footprint.polygon);
        const centroid = (area.centroid ?? calculatePolygonCentroid(coordinates) ?? coordinates[0]) as [number, number];
        return {
          id: `${record.id}-${area.id}`,
          label: `${record.parking.name} (${area.name})`,
          street: record.parking.address,
          variant: "custom" as const,
          coordinates,
          centroid,
          source: "custom" as const,
        };
      }),
    );
  }, [customRecords]);

  const polygons = useMemo<MapPolygonEntry[]>(() => [...basePolygons, ...customPolygons], [basePolygons, customPolygons]);
  const visiblePolygons = useMemo<MapPolygonEntry[]>(() => {
    if (keepAllPolygonsVisible || !activeMarkerId) {
      debugLog("visible polygons: all", { count: polygons.length });
      return polygons;
    }
    const active = polygons.filter((polygon) => polygon.id === activeMarkerId);
    const customs = polygons.filter((polygon) => polygon.source === "custom");
    debugLog("visible polygons: active + custom", { active: active.length, custom: customs.length });
    return active.length ? [...active, ...customs] : polygons;
  }, [activeMarkerId, keepAllPolygonsVisible, polygons]);
  const customPolygonSignature = useMemo(() => customPolygons.map((polygon) => polygon.id).join("|"), [customPolygons]);
  const polygonShapeSignature = useMemo(
    () =>
      polygons
        .map(
          (polygon) =>
            `${polygon.id}:${polygon.coordinates
              .map((point) => `${point[0].toFixed(6)},${point[1].toFixed(6)}`)
              .join(";")}`,
        )
        .join("|"),
    [polygons],
  );

  const featureCollection = useMemo(
    () => buildFeatureCollection(visiblePolygons, activeMarkerId),
    [visiblePolygons, activeMarkerId],
  );

  const mapRef = useRef<MapRef | null>(null);
  const lastViewRef = useRef<string>("initial");
  const activeMarkerRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof selectedMarkerId === "undefined") {
      return;
    }
    setActiveMarkerId(selectedMarkerId ?? null);
  }, [selectedMarkerId]);

  useEffect(() => {
    activeMarkerRef.current = activeMarkerId;
    debugLog("set active marker", { activeMarkerId });
  }, [activeMarkerId]);

  useEffect(() => {
    lastViewRef.current = "initial";
    debugLog("reset view ref", { markerSignature, customPolygonSignature, polygonShapeSignature });
  }, [markerSignature, customPolygonSignature, polygonShapeSignature]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handler: EventListener = (event) => {
      const detail = (event as CustomEvent<ParkingHoverEventDetail>).detail;
      setActiveMarkerId(detail?.markerId ?? null);
      debugLog("hover event", { markerId: detail?.markerId ?? null });
    };

    window.addEventListener(PARKING_HOVER_EVENT, handler);

    return () => {
      window.removeEventListener(PARKING_HOVER_EVENT, handler);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !mapReady) {
      return;
    }

    if (!focusSelectedMarker) {
      return;
    }

    if (activeMarkerId) {
      const polygon = polygons.find((entry) => entry.id === activeMarkerId);
      if (polygon) {
        focusOnPolygon(map, polygon);
        lastViewRef.current = activeMarkerId;
        debugLog("focus active polygon", { id: activeMarkerId });
      }
      return;
    }
  }, [activeMarkerId, focusSelectedMarker, polygons, mapReady]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !mapReady) {
      return;
    }

    if (activeMarkerRef.current && focusSelectedMarker) {
      return;
    }

    fitMapToPolygons(map, polygons);
    lastViewRef.current = "bounds";
    debugLog("fit polygons (shape change)", { polygonShapeSignature, polygonCount: polygons.length });
  }, [focusSelectedMarker, polygonShapeSignature, polygons, mapReady]);

  const handleMapLoad = useCallback(() => {
    setMapReady(true);
    lastViewRef.current = "initial";
    debugLog("map loaded", { polygons: visiblePolygons.length, activeMarkerId });
  }, [visiblePolygons.length, activeMarkerId]);

  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (!mapReady) {
        return;
      }
      const feature = event.features?.[0];
      const id = feature?.properties?.id;
      if (typeof id === "string") {
        setActiveMarkerId(id);
        onMarkerSelect?.(id);
        emitHoverEvent(id);
        debugLog("map click - set active", { id });
      } else {
        setActiveMarkerId(null);
        onMarkerSelect?.(null);
        emitHoverEvent(null);
      }
    },
    [mapReady, onMarkerSelect],
  );

  const handleMouseEnter = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      map.getCanvas().style.cursor = "pointer";
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      map.getCanvas().style.cursor = "";
    }
  }, []);

  const mapStyle = resolveStyleUrl();
  const initialViewState = useMemo(
    () => ({
      longitude: 18.77878,
      latitude: 52.57699,
      zoom: 15,
    }),
    [],
  );

  return (
    <div className={className ?? "h-full w-full"}>
      <MapView
        ref={mapRef}
        mapLib={maplibregl}
        mapStyle={mapStyle}
        initialViewState={initialViewState}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        interactiveLayerIds={["parking-polygons-fill", "parking-polygons-outline"]}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <NavigationControl position="top-right" showCompass={false} />
        <Source id="parking-polygons" type="geojson" data={featureCollection}>
          <Layer
            id="parking-polygons-fill"
            type="fill"
          paint={{
            "fill-color": [
              "case",
              ["==", ["get", "variant"], "highlight"],
              "#34d399",
              ["==", ["get", "variant"], "custom"],
              "#f472b6",
              "#60a5fa",
            ],
            "fill-opacity": [
              "case",
              ["==", ["get", "active"], true],
              0.55,
              0.28,
            ],
          }}
        />
        <Layer
          id="parking-polygons-outline"
          type="line"
          paint={{
            "line-color": [
              "case",
              ["==", ["get", "variant"], "highlight"],
              "#059669",
              ["==", ["get", "variant"], "custom"],
              "#db2777",
              "#2563eb",
            ],
            "line-width": [
              "case",
              ["==", ["get", "active"], true],
              3,
              1.5,
            ],
          }}
        />
        <Layer
          id="parking-polygons-label"
          type="symbol"
          layout={{
            "text-field": ["coalesce", ["get", "label"], ""],
            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
            "text-offset": [0, 0.6],
            "text-anchor": "top",
            "text-size": 12,
          }}
          paint={{
            "text-color": "#0f172a",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.2,
            "text-halo-blur": 0.5,
          }}
        />
      </Source>
      </MapView>
    </div>
  );
}
